import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { CreditCard, Plus, Building, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

// All products listed individually - no "All Foundation Features" etc.
const TIER_PRODUCTS = {
  foundation: [
    'Secure Email Gateway',
    'M365 Impersonation Protection',
    'M365 Cloud Backup',
    'Threat Exposure Management',
    'Security Awareness Training',
    'Phishing Simulations'
  ],
  advanced: [
    'Secure Email Gateway',
    'M365 Impersonation Protection',
    'M365 Cloud Backup',
    'Threat Exposure Management',
    'Security Awareness Training',
    'Phishing Simulations',
    'Vulnerability Scanning',
    'Managed Endpoint Protection',
    'Managed Firewall',
    'Dark Web Monitoring',
    'Web Filtering & Monitoring',
    'Annual Cyber Audit'
  ],
  complete: [
    'Secure Email Gateway',
    'M365 Impersonation Protection',
    'M365 Cloud Backup',
    'Threat Exposure Management',
    'Security Awareness Training',
    'Phishing Simulations',
    'Vulnerability Scanning',
    'Managed Endpoint Protection',
    'Managed Firewall',
    'Dark Web Monitoring',
    'Web Filtering & Monitoring',
    'Annual Cyber Audit',
    'Security Operations Centre (SOC)',
    '24/7 Monitoring & Remediation',
    '7-Day Ransomware Rollback',
    'Annual Penetration Testing',
    'Network Detection & Response',
    'Secure Remote Access',
    'Data Backup & Recovery'
  ]
};

function AdminSubscriptions() {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    company_id: '',
    tier: 'foundation',
    status: 'active',
    start_date: new Date().toISOString().split('T')[0],
    renewal_date: '',
    monthly_cost: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subsRes, localCompaniesRes, chartMogulRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/subscriptions`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/companies`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/chartmogul/customers`, { credentials: 'include' })
      ]);

      if (subsRes.ok) {
        const subsData = await subsRes.json();
        setSubscriptions(subsData);
      }

      let allCompanies = [];
      
      // Add local companies
      if (localCompaniesRes.ok) {
        const localData = await localCompaniesRes.json();
        allCompanies = localData.map(c => ({
          ...c,
          source: 'local'
        }));
      }
      
      // Add ChartMogul customers
      if (chartMogulRes.ok) {
        const cmData = await chartMogulRes.json();
        const cmCompanies = (cmData.customers || []).map(c => ({
          company_id: c.chartmogul_uuid,
          name: c.name || c.company,
          source: 'chartmogul'
        }));
        allCompanies = [...allCompanies, ...cmCompanies];
      }
      
      // Sort by name
      allCompanies.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(allCompanies);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to delete this subscription? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Subscription deleted successfully');
        fetchData();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete subscription');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const products = TIER_PRODUCTS[formData.tier];
      const response = await fetch(`${BACKEND_URL}/api/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          products,
          active_products: products,
          start_date: new Date(formData.start_date).toISOString(),
          renewal_date: formData.renewal_date ? new Date(formData.renewal_date).toISOString() : null,
          monthly_cost: formData.monthly_cost ? parseFloat(formData.monthly_cost) : null
        })
      });

      if (response.ok) {
        toast.success('Subscription created successfully');
        setDialogOpen(false);
        setFormData({
          company_id: '',
          tier: 'foundation',
          status: 'active',
          start_date: new Date().toISOString().split('T')[0],
          renewal_date: '',
          monthly_cost: ''
        });
        fetchData();
      } else {
        throw new Error('Failed to create subscription');
      }
    } catch (error) {
      toast.error('Failed to create subscription');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Subscriptions">
        <div className="flex items-center justify-center h-64">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-secondary border-r-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Subscriptions">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <CreditCard className="h-8 w-8 text-secondary" />
            <h2 className="text-2xl font-mono font-bold">Subscription Management</h2>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-mono">
                <Plus className="h-4 w-4 mr-2" />
                Add Subscription
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-mono">Create Subscription</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="company" className="font-mono">Company *</Label>
                  <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })}>
                    <SelectTrigger className="font-mono mt-1">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {companies.map((company) => (
                        <SelectItem key={company.company_id} value={company.company_id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="tier" className="font-mono">Tier *</Label>
                  <Select value={formData.tier} onValueChange={(value) => setFormData({ ...formData, tier: value })}>
                    <SelectTrigger className="font-mono mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="foundation">Foundation</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                      <SelectItem value="complete">Complete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="status" className="font-mono">Status *</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="font-mono mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date" className="font-mono">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      className="font-mono mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="renewal_date" className="font-mono">Renewal Date</Label>
                    <Input
                      id="renewal_date"
                      type="date"
                      value={formData.renewal_date}
                      onChange={(e) => setFormData({ ...formData, renewal_date: e.target.value })}
                      className="font-mono mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="monthly_cost" className="font-mono">Monthly Cost (£)</Label>
                  <Input
                    id="monthly_cost"
                    type="number"
                    step="0.01"
                    value={formData.monthly_cost}
                    onChange={(e) => setFormData({ ...formData, monthly_cost: e.target.value })}
                    className="font-mono mt-1"
                    placeholder="e.g., 299.99"
                  />
                </div>

                <Button type="submit" className="w-full font-mono">
                  Create Subscription
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {subscriptions.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <CreditCard className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-mono font-bold mb-2">No Subscriptions</h3>
            <p className="text-muted-foreground">Create your first subscription to get started</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/10 border-b border-border">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Tier</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Cost</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-mono font-bold uppercase tracking-wider">Renewal</th>
                    <th className="px-6 py-3 text-right text-xs font-mono font-bold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {subscriptions.map((sub) => {
                    const company = companies.find(c => c.company_id === sub.company_id);
                    const activeCount = sub.active_products?.length || sub.products?.length || 0;
                    const totalCount = sub.products?.length || 0;
                    return (
                      <tr key={sub.subscription_id} className="hover:bg-muted/5">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-muted-foreground" />
                            <span className="font-mono font-bold">{company?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono font-bold text-secondary">{sub.tier.toUpperCase()}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-mono border ${
                            sub.status === 'active' ? 'bg-primary/10 text-primary border-primary/20' :
                            sub.status === 'suspended' ? 'bg-destructive/10 text-destructive border-destructive/20' :
                            'bg-muted/10 text-muted-foreground border-muted/20'
                          }`}>
                            {sub.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono">{sub.monthly_cost ? `£${sub.monthly_cost}` : 'POA'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-muted-foreground">
                            {new Date(sub.start_date).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-mono text-muted-foreground">
                              {sub.renewal_date ? new Date(sub.renewal_date).toLocaleDateString() : '-'}
                            </span>
                            <button
                              onClick={() => window.location.href = `/admin/subscriptions/${sub.subscription_id}`}
                              className="text-xs font-mono text-secondary hover:text-secondary/80 text-left"
                            >
                              Manage Products ({activeCount}/{totalCount})
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(sub.subscription_id)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminSubscriptions;